import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Link, NavLink } from 'react-router-dom';
import { Bell, Search, Menu, Crown, Sparkles, MessageCircle, LogOut, PaintBucket, Store, Users, Home, Layers, Infinity, Settings, Grid3x3, ChevronDown, Plus, Lock, Camera, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { NotificationBadge } from '@/components/notifications/NotificationBadge';
import { Navigation } from './Navigation';
import NavSidebar from './NavSidebar';
import NavBottomBar from './NavBottomBar';
import { useMediaQuery } from '@/hooks/use-media-query';
import { desktopNavItems, mobileNavItems } from './config';
import { supabase } from '@/integrations/supabase/client';
import debounce from 'lodash/debounce';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useMoodboards } from '@/hooks/use-moodboards';
import Portal from '@/components/ui/portal';
import { cn } from '@/lib/utils';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import UserDropdown from './UserDropdown';
import { LogoText } from './LogoText';
import AuraBadge from '@/components/common/AuraBadge';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import WelcomeShareCard from '@/components/onboarding/WelcomeShareCard';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer';

/**
 * Enhanced Navigation that combines modern Navigation components with
 * search functionality and notifications from MainNav
 */
interface EnhancedNavigationProps {
  children: React.ReactNode;
}

export const EnhancedNavigation: React.FC<EnhancedNavigationProps> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { showNotifications, setShowNotifications } = useNotifications();
  const { user, profile, logout } = useAuth();
  const { isAdmin, isPremium, isPartner } = useUserRoles();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  
  // Search functionality
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  
  // Menu drawer state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showTopicsDialog, setShowTopicsDialog] = useState(false);
  const [showFounderCard, setShowFounderCard] = useState(false);
  const [expandedMoodboards, setExpandedMoodboards] = useState(false);
  const [userResults, setUserResults] = useState<any[]>([]);
  const [tagResults, setTagResults] = useState<string[]>([]);

  // Allow external components (e.g., TopBar inside PageLayout) to open this drawer
  useEffect(() => {
    const openHandler = () => setIsMenuOpen(true);
    const closeHandler = () => setIsMenuOpen(false);
    window.addEventListener('nav:open-menu', openHandler as EventListener);
    window.addEventListener('nav:close-menu', closeHandler as EventListener);
    return () => {
      window.removeEventListener('nav:open-menu', openHandler as EventListener);
      window.removeEventListener('nav:close-menu', closeHandler as EventListener);
    };
  }, []);
  
  // Fetch moodboards for menu
  const { data: moodboards = [] } = useMoodboards(user?.id);
  
  // Fetch joined communities for sidebar
  const { data: joinedCommunities } = useQuery({
    queryKey: ['sidebar-joined-communities', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('user_community_memberships')
        .select(`
          community:communities (
            id, name, display_name, icon, avatar_url, is_fomo_official, post_count
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data || []).map((item: any) => item.community).filter(Boolean);
    },
    enabled: !!user?.id
  });

  // Fetch all communities for sidebar (discovery)
  const { data: communities } = useQuery({
    queryKey: ['sidebar-communities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('id, name, display_name, icon, avatar_url, is_fomo_official, is_admin_curated, is_auto_generated, post_count')
        .limit(50);
      if (error) throw error;
      // Filter: Show only FOMO official for now (hide auto-generated)
      const filtered = (data || []).filter((c: any) => c.is_fomo_official === true);
      // Sort: official, admin curated, post_count desc
      const sorted = filtered.sort((a: any, b: any) => {
        if (a.is_fomo_official && !b.is_fomo_official) return -1;
        if (!a.is_fomo_official && b.is_fomo_official) return 1;
        if ((a.is_admin_curated ? 1 : 0) !== (b.is_admin_curated ? 1 : 0)) {
          return (b.is_admin_curated ? 1 : 0) - (a.is_admin_curated ? 1 : 0);
        }
        return (b.post_count || 0) - (a.post_count || 0);
      });
      return sorted;
    }
  });

  // Search functionality from MainNav
  const searchUsers = async (query: string) => {
    if (!query || query.trim() === '') {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const sanitizedQuery = query.trim();
      
      const { data, error } = await supabase
        .from('public_profiles')
        .select('id, username, display_name, profile_image_url')
        .or(`username.ilike.${sanitizedQuery}%,username.ilike.%${sanitizedQuery}%,display_name.ilike.%${sanitizedQuery}%`)
        .limit(8);

      if (error) {
        console.error('Search error:', error);
      } else {
        setSearchResults(data || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = useMemo(
    () => debounce((query: string) => searchUsers(query), 300),
    []
  );

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleSelectUser = (username: string) => {
    setSearchOpen(false);
    navigate(`/user/${username}`);
  };

  // Skip rendering on specific pages
  const isLandingPage = pathname === "/landing";
  const isAuthPage = pathname === "/auth";
  const isProfileCreationPage = pathname === "/create-username" || pathname === "/create-profile";
  const isNeedInvitePage = pathname === "/need-invite";
  const isSplineLanding = pathname === '/spline-landing';
  // Pages that render their own TopBar component (avoid double top bars)
  const pageProvidesOwnHeader = (
    pathname.startsWith('/bulletins') ||
    pathname.startsWith('/loops') ||
    pathname.startsWith('/top-follows') ||
    pathname.startsWith('/profile') ||
    pathname.startsWith('/user/') ||
    pathname.startsWith('/2/') || // BentoProfile
    pathname.startsWith('/settings') ||
    pathname.startsWith('/drops') ||
    pathname.startsWith('/communities') ||
    pathname.startsWith('/community/')
  );
  
  const shouldHideNav = isLandingPage || isAuthPage || isProfileCreationPage || isNeedInvitePage || isSplineLanding;
  
  if (shouldHideNav) {
    // On pages like /landing or auth-related routes, hide the chrome but still render children
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen w-full bg-[#1A1A1B]">
      {/* Desktop sidebar navigation */}
      {isDesktop && (
        <div className="h-screen fixed left-0 top-0 z-20 flex-none overflow-hidden w-64 border-r border-white/10 bg-[#1A1A1B]" style={{ pointerEvents: 'auto' }}>
          <div className="flex flex-col h-full">
            {/* FOMO Logo above navigation (single logo on page) */}
            <div className="flex justify-start items-center py-3 pl-3 pr-2">
              <img
                src="/fomo_2025.png"
                alt="FOMO Logo"
                className="h-14 w-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
                onClick={() => navigate('/bulletins')}
                style={{ cursor: 'pointer' }}
              />
            </div>
            {/* User cluster (below logo) - only show after onboarding */}
            {user && profile && profile.has_completed_onboarding && (
              <div className="px-3 pb-2">
                <div className="flex items-center gap-3">
                  {profile?.profile_image_url && (
                    <button
                      onClick={() => {
                        if (profile?.username) navigate(`/user/${profile.username}`);
                        else navigate('/create-username');
                      }}
                      className="relative inline-flex items-center justify-center rounded-full p-[2px]"
                      aria-label="Open profile"
                    >
                      <span className={cn(
                        'absolute -inset-[2px] rounded-full',
                        isAdmin && 'bg-gradient-to-t from-[#F97316] to-[#FEF7CD]',
                        isPremium && 'bg-gradient-to-r from-[#1A1F2C] via-[#7E69AB] to-[#FFD700]'
                      )} />
                      <span className="relative rounded-full bg-background p-[2px] block">
                        <img src={profile.profile_image_url} alt="Your avatar" className="w-10 h-10 rounded-full object-cover" />
                      </span>
                    </button>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-semibold truncate max-w-[8rem]">{profile?.display_name || profile?.username || 'User'}</span>
                    </div>
                    <div className="mt-1 inline-flex items-center gap-1.5">
                      <span className="text-xs font-medium text-white truncate max-w-[10rem]">@{profile?.username || 'username'}</span>
                      {isAdmin && (
                        <VerifiedBadge type="admin" size={16} className="inline-block" shadow={false} />
                      )}
                      {isPartner && (
                        <VerifiedBadge type="partner" size={16} className="inline-block" shadow={false} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <AuraBadge value={profile?.elo_rating ?? 0} size="md" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/settings')}
                    aria-label="Open settings"
                    className="h-8 px-2 hover:bg-white/10"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              <NavSidebar
                items={desktopNavItems.map(item => {
                  // Dynamically add communities as children to the Communities nav item
                  if (item.id === 'communities') {
                    const communityChildren = [];
                    
                    // Add joined communities first with a separator
                    if (joinedCommunities && joinedCommunities.length > 0) {
                      communityChildren.push({
                        id: 'joined-header',
                        label: '── Your Communities ──',
                        icon: () => null,
                        href: '#',
                        disabled: true
                      } as any);
                      
                      joinedCommunities.forEach((community: any) => {
                        communityChildren.push({
                          id: `joined-community-${community.name}`,
                          label: community.display_name,
                          icon: () => (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full overflow-hidden bg-muted ring-2 ring-primary/30">
                              {community.avatar_url ? (
                                <img src={community.avatar_url} alt={community.display_name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-[10px]">#</span>
                              )}
                            </span>
                          ),
                          href: `/community/${community.name}`,
                          disabled: false
                        });
                      });
                    }
                    
                    // Add all communities section
                    if (communities && communities.length > 0) {
                      if (joinedCommunities && joinedCommunities.length > 0) {
                        communityChildren.push({
                          id: 'discover-header',
                          label: '── Discover ──',
                          icon: () => null,
                          href: '#',
                          disabled: true
                        } as any);
                      }
                      
                      communities.forEach((community: any) => {
                        communityChildren.push({
                          id: `community-${community.name}`,
                          label: community.display_name,
                          icon: () => (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full overflow-hidden bg-muted">
                              {community.avatar_url ? (
                                <img src={community.avatar_url} alt={community.display_name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-[10px]">#</span>
                              )}
                            </span>
                          ),
                          href: `/community/${community.name}`,
                          disabled: false
                        });
                      });
                    }
                    
                    return {
                      ...item,
                      children: communityChildren
                    };
                  }
                  // Handle partnerOnly items - gray out for non-partner/admin users
                  if (item.partnerOnly) {
                    const isPartnerOrAdmin = isPartner || isAdmin;
                    return {
                      ...item,
                      disabled: !isPartnerOrAdmin,
                    };
                  }
                  return item;
                })}
                onNavigate={(href) => navigate(href)}
                className="h-full bg-transparent"
                // Make communities always expanded
                alwaysExpandedIds={['communities']}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main content area with optional utility header */}
      <div className="flex flex-col flex-1 min-h-screen w-full ml-0 md:ml-64 pt-0" style={{ position: 'relative' }}>
        {/* Top utility bar for search, notifications, and user profile (hidden on pages that provide their own header) */}
        {!pageProvidesOwnHeader && (
          <header className="sticky top-0 z-30 w-full border-b border-white/10 bg-[#1A1A1B] shadow-[0_4px_20px_-10px_rgba(0,0,0,0.6)]">
            <div className="flex h-16 items-center px-4 md:px-6">
              <div className="flex w-full justify-between items-center">
                <div className="flex items-center gap-3">{!isDesktop && <LogoText />}</div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 hover:bg-white/10"
                    onClick={() => setSearchOpen(true)}
                  >
                    <Search className="h-4 w-4" />
                  </Button>

                  {user && (
                    <>
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="relative h-9 w-9 hover:bg-white/10"
                          data-notifications-toggle
                          onClick={() => setShowNotifications(!showNotifications)}
                        >
                          <Bell className="h-4 w-4" />
                          <NotificationBadge />
                        </Button>
                      </div>
                      
                      {/* Menu button - opens drawer */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="relative h-9 w-9 hover:bg-white/10"
                        onClick={() => setIsMenuOpen(true)}
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Content area - children will be rendered here */}
        <div id="app-scroll-container" className="flex-1 overflow-y-auto relative z-10">
          {children}
        </div>

        {/* Mobile bottom navigation - only shown on smaller screens */}
        {!isDesktop && (
          <div className="fixed bottom-0 left-0 right-0 z-40">
            <NavBottomBar
              items={mobileNavItems.map(item => {
                if (item.id === 'account' && profile?.username) {
                  return { ...item, href: `/user/${profile.username}` };
                }
                // Handle partnerOnly items - gray out for non-partner/admin users
                if (item.partnerOnly) {
                  const isPartnerOrAdmin = isPartner || isAdmin;
                  return {
                    ...item,
                    disabled: !isPartnerOrAdmin,
                  };
                }
                return item;
              })}
              onNavigate={(href) => navigate(href)}
            />
          </div>
        )}
      </div>
      
      {/* Menu drawer */}
      <Drawer open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DrawerContent className="bg-[#1A1A1B] border-t-2 border-[#343536] shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.3)] max-h-[75vh] max-w-2xl mx-auto">
          <div className="overflow-y-auto flex flex-col custom-scrollbar">
            {/* User hero section - Full width, no padding */}
            <section className="relative z-0">
              {/* Banner - Taller with profile overlay */}
              <div className="relative h-40 bg-cover bg-center overflow-hidden" style={{ backgroundImage: `url(${profile?.banner_image_url || ''})` }}>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1A1A1B]" />
                
                {/* Profile info overlaid on banner */}
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end gap-3">
                  {profile?.profile_image_url && (
                    <div className="relative cursor-pointer mb-1" onClick={() => { navigate(`/profile/${profile?.username}`); setIsMenuOpen(false); }}>
                      <div
                        className={cn(
                          'rounded-full p-[3px]',
                          isAdmin && 'bg-gradient-to-t from-[#F97316] to-[#FEF7CD]',
                          isPremium && 'bg-gradient-to-r from-[#1A1F2C] via-[#7E69AB] to-[#FFD700]'
                        )}
                      >
                        <div className="rounded-full bg-[#1A1A1B] p-[2px]">
                          <img src={profile.profile_image_url} alt="Your avatar" className="w-14 h-14 rounded-full object-cover" />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 cursor-pointer mb-1" onClick={() => { navigate(`/profile/${profile?.username}`); setIsMenuOpen(false); }}>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold truncate text-white drop-shadow-lg">{profile?.display_name || profile?.username || 'User'}</span>
                      {isAdmin && (
                        <VerifiedBadge type="admin" size={14} className="inline-block" shadow={false} />
                      )}
                      {isPartner && (
                        <VerifiedBadge type="partner" size={14} className="inline-block" shadow={false} />
                      )}
                    </div>
                    <span className="text-xs text-white/90 truncate drop-shadow-md">@{profile?.username}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 mb-1">
                    <Button
                      onClick={() => setShowTopicsDialog(true)}
                      aria-label="Open your topics"
                      className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white border-0 shadow-lg h-7 px-2 text-xs"
                      size="sm"
                    >
                      <Sparkles className="w-3 h-3 mr-1" /> Topics
                    </Button>
                    <Button
                      onClick={() => setShowFounderCard(true)}
                      aria-label="View founder card"
                      className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white border-0 shadow-lg h-7 px-2 text-xs"
                      size="sm"
                    >
                      <Award className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      onClick={() => { navigate('/settings'); setIsMenuOpen(false); }} 
                      aria-label="Open settings"
                      className="h-7 px-2 text-xs shadow-lg"
                    >
                      <Settings className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            <div className="p-3">

            {/* Quick Actions - Discord & Logout */}
            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                onClick={() => window.open('https://discord.gg/ErRUrKBCcb', '_blank')}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition text-xs"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Join Discord
              </button>
              <button
                onClick={logout}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 transition text-xs"
              >
                <LogOut className="w-3.5 h-3.5" /> Log out
              </button>
            </div>

            {/* Quick Navigation - Compact */}
            <div className="mt-3">
              <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Navigate</h3>
              <div className="flex gap-1.5 flex-wrap">
                <NavLink 
                  to="/bulletins" 
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors text-xs"
                >
                  <Camera className="w-3.5 h-3.5 text-primary" />
                  <span>Artstream</span>
                </NavLink>
                
                <NavLink 
                  to="/loops" 
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-violet-300/20 bg-violet-500/5 hover:bg-violet-500/10 transition-colors text-xs"
                >
                  <Infinity className="w-3.5 h-3.5 text-violet-400" />
                  <span>Loops</span>
                </NavLink>
                
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-muted/5 cursor-not-allowed text-xs text-muted-foreground">
                  <PaintBucket className="w-3.5 h-3.5" />
                  <span>Canvas</span>
                  <span className="text-[10px] italic opacity-70">Soon</span>
                </div>
                
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-muted/5 cursor-not-allowed text-xs text-muted-foreground">
                  <Store className="w-3.5 h-3.5" />
                  <span>Sticker Shop</span>
                  <span className="text-[10px] italic opacity-70">Soon</span>
                </div>
              </div>
            </div>

            {/* Moodboards - Hero Section */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                    <Grid3x3 className="w-4 h-4 text-pink-400" />
                    Your Moodboards
                  </h3>
                  <p className="text-[10px] text-gray-400 mt-0.5">Save and organize your favorite content</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {moodboards.length > 6 && !expandedMoodboards && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-pink-400 hover:bg-pink-500/10 text-xs h-7 px-2"
                      onClick={() => setExpandedMoodboards(true)}
                    >
                      <Layers className="w-3 h-3 mr-1" />
                      View All
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white text-xs h-7 px-2"
                    onClick={() => { navigate('/bulletins'); setIsMenuOpen(false); }}
                  >
                    <Plus className="w-3 h-3 mr-0.5" />
                    New
                  </Button>
                </div>
              </div>
              
              {moodboards.length === 0 ? (
                <div className="rounded-xl border border-pink-400/20 bg-pink-500/5 p-4 text-center">
                  <Grid3x3 className="w-8 h-8 text-pink-400/50 mx-auto mb-2" />
                  <h4 className="text-xs font-semibold text-white mb-1.5">No moodboards yet</h4>
                  <p className="text-[10px] text-gray-400 mb-3 max-w-xs mx-auto">
                    Create your first moodboard to start collecting and organizing content you love. 
                    Click the bookmark icon on any post to save it!
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-pink-400/30 text-pink-400 hover:bg-pink-500/10 text-xs h-7"
                    onClick={() => { navigate('/bulletins'); setIsMenuOpen(false); }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Create First Moodboard
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(expandedMoodboards ? moodboards : moodboards.slice(0, 6)).map((moodboard) => (
                    <motion.button
                      key={moodboard.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        navigate(`/moodboards/${moodboard.id}`);
                        setIsMenuOpen(false);
                      }}
                      className="relative aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-pink-400/50 transition-all group"
                    >
                      {moodboard.cover_image_url ? (
                        <img
                          src={moodboard.cover_image_url}
                          alt={moodboard.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-pink-500/10 to-purple-500/10">
                          {moodboard.icon}
                        </div>
                      )}
                      
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                      
                      {/* Item count - top right, subtle */}
                      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-black/50 backdrop-blur-sm">
                        <p className="text-[9px] text-white/70 font-medium">{moodboard.item_count || 0}</p>
                      </div>
                      
                      {/* Name - always visible at bottom left */}
                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <div className="flex items-center gap-1">
                          <p className="text-xs font-bold text-white truncate flex-1 drop-shadow-lg">{moodboard.name}</p>
                          {moodboard.is_private && (
                            <Lock className="w-2.5 h-2.5 text-white/70 flex-shrink-0 drop-shadow-lg" />
                          )}
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
              
              {/* Show Less button when expanded */}
              {expandedMoodboards && moodboards.length > 6 && (
                <div className="mt-2 text-center">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-gray-400 hover:text-pink-400 hover:bg-pink-500/10 text-xs h-7"
                    onClick={() => setExpandedMoodboards(false)}
                  >
                    <ChevronDown className="w-3 h-3 mr-1 rotate-180" />
                    Show Less
                  </Button>
                </div>
              )}
            </div>

            </div>
          </div>
        </DrawerContent>
      </Drawer>
      
      {/* Search dialog */}
      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput 
          placeholder="Search for users..." 
          value={searchQuery}
          onValueChange={handleSearchInputChange}
        />
        <CommandList>
          <CommandEmpty>
            {isSearching ? "Searching..." : "No users found"}
          </CommandEmpty>
          
          <CommandGroup heading="Users">
            {searchResults.map((user) => (
              <CommandItem 
                key={user.id}
                onSelect={() => handleSelectUser(user.username)}
                className="flex items-center gap-2"
              >
                <div className="w-6 h-6 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  {user.profile_image_url && (
                    <img 
                      src={user.profile_image_url} 
                      alt={user.username} 
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="font-medium">{user.display_name || user.username}</span>
                  <span className="text-xs text-muted-foreground">@{user.username}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Founder Card Modal */}
      {showFounderCard && profile && (
        <WelcomeShareCard
          profile={profile}
          onClose={() => setShowFounderCard(false)}
        />
      )}
    </div>
  );
};
